import { useEditorContext, EditorView, SIDEBAR_VIEWS_WITH_CHILDREN } from './context';
import { ChapterTree } from '../workspace/components/ChapterTree';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { GlobalSearchBox, useGlobalSearch, matchTotal, SearchMatch } from './GlobalSearch';
import { PANE_LABEL, PANE_ORDER, type PaneId } from './paneLayout';
import {
  MessageSquare, BookText, ChevronRight, Home, Settings, Sun, Moon, BookOpen, PanelLeftClose,
} from 'lucide-react';

/**
 * 侧边栏一级菜单 —— **两项，都是一级入口**。
 *
 * 「设定管理 / 资料库 / 伏笔追踪」已迁到右侧的图标栏（`PaneRail`）里，
 * 它们不再是"切页"而是"在右侧展开一块可对照的面板"：作者写到一半要看设定时，
 * 正文不必让位（这正是本次改造的目的）。
 *
 * - `章节列表`：**可展开**，子菜单是章节树（"在这个工作区里可直接跳转的多个目标"）。
 * - `AI 对话`：**叶子入口**（没有子菜单）。会话列表（含「新建对话」）搬进了
 *   AI 对话视图内部 —— 挂在侧栏子菜单里时，侧栏一收起就会造成"进去只有一句提示、
 *   连新建对话都点不到"的死路。详见 `AIChatContent`。
 *
 * 版本历史仍然没有侧栏入口：它的入口是编辑器工具栏的「历史版本」按钮
 * （从正文跳进历史更自然），进入后占据中心区域。
 */
const MENU_ITEMS: { id: EditorView; label: string; icon: React.ReactNode }[] = [
  { id: 'chapters', label: '章节列表', icon: <BookText size={18} /> },
  { id: 'ai-chat', label: 'AI 对话', icon: <MessageSquare size={18} /> },
];

const MENUS = MENU_ITEMS.map(item => ({
  ...item,
  hasChildren: SIDEBAR_VIEWS_WITH_CHILDREN.has(item.id),
}));

export function EditorSidebar({
  onShowSettings,
  isDarkMode,
  onToggleDarkMode,
  onNavigateHome,
  onCollapseSidebar,
  collapsed,
  search,
}: {
  onShowSettings: () => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onNavigateHome: () => void;
  /** 收起侧栏（收起后侧栏以左侧窄栏里的一个按钮呈现，见 `Editor/index.tsx`） */
  onCollapseSidebar: () => void;
  collapsed: boolean;
  /**
   * 全局搜索实例。**由 `EditorLayout` 持有、以 prop 传进来**（不在这里 new）：
   * 视图内的会话列表也要用同一份过滤结果，两处各自调 `useGlobalSearch()` 会得到
   * 两个互不相干的状态（各有各的 `useState`），搜索框的过滤就传不到视图里。
   */
  search: ReturnType<typeof useGlobalSearch>;
}) {
  const {
    activeView, setActiveView,
    expandedMenus, setMenuExpanded,
    showPane,
  } = useEditorContext();

  const { bookTitle } = useWorkspace();

  const filterMode = search.filterActive;
  const m: SearchMatch | null = search.match;
  const filtering = filterMode && !!m;

  const menus = MENUS;

  /**
   * 全局搜索过滤时，这个一级入口是否该显示。
   */
  const menuHasMatch = (id: EditorView): boolean => {
    if (!m) return false;
    switch (id) {
      case 'chapters': return m.chapterIds.size > 0;
      case 'ai-chat': return m.conversationIds.size > 0;
      default: return false;
    }
  };

  /**
   * 命中「设定管理 / 资料库 / 伏笔追踪」的面板。
   *
   * 这三项已不在侧栏，若不给入口，用户搜到一个人物名时侧栏会**空着**、
   * 看上去像"什么都没搜到"（而结果其实在右侧面板里）。所以这里列出"结果在哪块面板"，
   * 点一下就把那块面板拉出来。
   */
  const paneHits: PaneId[] = !m ? [] : PANE_ORDER.filter((id) => {
    switch (id) {
      case 'settings': return m.settingMatch || m.outline || m.volumeIds.size > 0;
      case 'data': return m.factionMatch || m.characterMatch || m.itemMatch;
      // 伏笔目前不进全局搜索索引（SearchMatch 无对应字段），故永不命中
      default: return false;
    }
  });

  const selectMenu = (id: EditorView) => {
    // 叶子入口（没有子菜单的，如 AI 对话）：
    //   - 不在该视图 → 切过去；**已经在了 → 回章节**（开关式，与左侧窄栏那枚按钮一致）。
    //     切回章节不会中断 AI 生成：该视图在 `Editor/index.tsx` 里是"打开过就常驻、
    //     只切 hidden"，组件不卸载。
    if (!SIDEBAR_VIEWS_WITH_CHILDREN.has(id)) {
      setActiveView(activeView === id ? 'chapters' : id);
      return;
    }
    const willExpand = !expandedMenus[id];
    setMenuExpanded(id, willExpand);
    if (willExpand) setActiveView(id);
  };

  /**
   * 子菜单内容。现在只剩章节树 —— 会话列表已搬进 AI 对话**视图内部**
   * （见 `AIChatContent` 的注释：挂在侧栏里会造成"侧栏收起就没法新建对话"的死路）。
   */
  const renderChildren = (id: EditorView) => {
    if (id !== 'chapters') return null;
    const fm = filtering ? m : null;
    return <ChapterTree filterChapterIds={fm ? fm.chapterIds : null} />;
  };

  const noMatches = filtering && matchTotal(m) === 0;

  return (
    <aside
      className={`editor-sidebar${collapsed ? ' editor-sidebar--collapsed' : ''}`}
    >
      <div className="editor-sidebar-brand" title={bookTitle || '小天书'}>
        <BookOpen size={18} />
        <span className="editor-sidebar-brand-title">{bookTitle || '小天书'}</span>
      </div>

      <div className="editor-sidebar-search-row">
        <GlobalSearchBox
          query={search.query}
          onChange={search.handleChange}
          onSubmit={search.submit}
          onClear={search.clear}
          onFocus={search.onFocus}
          onPick={search.pick}
          closeDropdown={search.closeDropdown}
          dropdownOpen={search.dropdownOpen}
          results={search.results}
          loading={search.loading}
        />
        <button
          className="sidebar-collapse-btn"
          onClick={onCollapseSidebar}
          title="收起侧栏"
          aria-label="收起侧栏"
          aria-expanded={!collapsed}
        >
          <PanelLeftClose size={16} />
        </button>
      </div>

      <div className="editor-sidebar-scroll">
        {filterMode && !m && (
          <div className="global-search-status">搜索中…</div>
        )}
        {noMatches && (
          <div className="global-search-status">未找到与“{search.query.trim()}”匹配的内容</div>
        )}
        {(!filterMode || m) && menus.map(menu => {
          if (filtering && !menuHasMatch(menu.id)) return null;
          const expanded = menu.hasChildren && (filtering ? true : expandedMenus[menu.id]);
          return (
            <div key={menu.id} className="sidebar-menu">
              <button
                className={`sidebar-nav-item${activeView === menu.id ? ' sidebar-nav-item--active' : ''}`}
                onClick={() => (filtering ? setActiveView(menu.id) : selectMenu(menu.id))}
              >
                <span className="sidebar-nav-icon">{menu.icon}</span>
                <span className="sidebar-nav-label">{menu.label}</span>
                {/* 叶子入口没有可展开的子菜单，不画箭头（画了会让人以为点了会展开） */}
                {menu.hasChildren && (
                  <ChevronRight
                    size={14}
                    className={`sidebar-nav-caret${expanded ? ' is-open' : ''}`}
                  />
                )}
              </button>
              {menu.hasChildren && expanded && (
                <div className="sidebar-menu-body">
                  {renderChildren(menu.id)}
                </div>
              )}
            </div>
          );
        })}

        {/* 命中右侧面板时给出入口：否则用户搜到设定/人物却只看到空侧栏 */}
        {filtering && paneHits.length > 0 && (
          <div className="sidebar-pane-hits">
            <div className="global-search-status">匹配结果在右侧面板</div>
            {paneHits.map(id => (
              <button
                key={id}
                className="sidebar-pane-hit-btn"
                onClick={() => showPane(id)}
                title={`打开「${PANE_LABEL[id]}」面板`}
              >
                {PANE_LABEL[id]}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="editor-sidebar-spacer" />

      {/* 底部横排控制条 */}
      <div className="sidebar-footer-row">
        <button className="sidebar-footer-btn" onClick={onNavigateHome} title="返回书架" aria-label="返回书架">
          <Home size={18} />
        </button>
        <button className="sidebar-footer-btn" onClick={onShowSettings} title="设置" aria-label="设置">
          <Settings size={18} />
        </button>
        <button className="sidebar-footer-btn" onClick={onToggleDarkMode} title={isDarkMode ? '日间模式' : '夜间模式'} aria-label={isDarkMode ? '日间模式' : '夜间模式'}>
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>
    </aside>
  );
}
